import { useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { UserContext } from "@context/user";
import { PAYMENT_MODEL } from "@constants/panel/common";
import useAxios from "@hooks/use-axios";

const isModelPicked = (model) => Boolean(model) && model !== PAYMENT_MODEL.NOT_SELECTED;

const useBrandSummary = (brandId) => {

    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true, showAlert: false });
    const { opsPaymentModel } = useContext(UserContext);
    const navigate = useNavigate();
    const [brand, setBrand] = useState(null);

    // A brand that has chosen a payment model sits on exactly one of them. Flipping the
    // topbar toggle remounts this page (see layouts/types/vertical-layout), so the guard
    // runs on the fresh fetch: a brand that isn't on the model now in view has nothing to
    // show here — its numbers belong to the other side of the book — so bounce to the
    // brand list, which the `x-payment-model` header has already re-scoped.
    //
    // A brand still on `not_selected` is the exception: it belongs to neither book yet,
    // and Ops has to be able to open it from either view.
    const fetchBrand = async () => {
        try {
            const res = await axiosInstance.get(`accounts/v1/${brandId}`);
            const result = res.data || res;
            const data = result.data || result;

            if (isModelPicked(data?.payment_model) && data.payment_model !== opsPaymentModel) {
                navigate("/brands", { replace: true });
                return;
            }
            setBrand(data);
        } catch {
            // The API scopes by the same header, so a brand outside the current model
            // can come back as a plain miss rather than a mismatch — same outcome.
            navigate("/brands", { replace: true });
        }
    };

    useEffect(() => {
        fetchBrand();
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    // An unset brand has no model of its own to read the page with, so it follows the
    // view it was opened from — under self payout that means no debit-note tab, column
    // or frequency, exactly as for a brand that has picked it.
    const effectiveModel = isModelPicked(brand?.payment_model) ? brand.payment_model : opsPaymentModel;

    return {
        brand,
        loading,
        isSelfPayout: effectiveModel === PAYMENT_MODEL.SELF_PAYOUT,
        refetch     : fetchBrand
    };
};

export default useBrandSummary;
